import { redirectToOAuth } from "@/lib/oauthHelpers";
import { ReactNode } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useMemo } from "react";
import { useContentSelectieContext } from "@/context/ContentSelectieContext";
import { getOAuthStateToken } from "@/lib/authRedirect";

interface LoginButtonProps {
  provider: "google" | "microsoft" | "apple";
  icon: ReactNode;
  label: string;
  size?: "md" | "lg";
}

export default function OAuthLoginButton({
  provider,
  icon,
  label,
  size = "md"
}: LoginButtonProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queryIntent = searchParams.get("intent");
  const queryToken = searchParams.get("token");
  const queryFrom = searchParams.get("from");
  const queryPlanId = searchParams.get("plan_id");
  const { profiel } = useContentSelectieContext();

  const token = useMemo(() => {
    return getOAuthStateToken(queryIntent, queryToken);
  }, [queryIntent, queryToken]);

  const currentPathWithQuery = useMemo(() => {
    const query = searchParams.toString();
    return query ? `${pathname}?${query}` : pathname;
  }, [pathname, searchParams]);

  const intent =
    queryIntent ??
    (pathname.startsWith("/library") ? currentPathWithQuery : "");

  const profielForState =
    pathname.startsWith("/library") && profiel ? profiel : undefined;
  const checkoutFrom =
    queryIntent === "checkout" && queryFrom ? queryFrom : undefined;
  const checkoutPlanId =
    queryIntent === "checkout" && queryPlanId ? queryPlanId : undefined;

  const login = () => {
    redirectToOAuth(
      provider,
      intent,
      undefined,
      token,
      profielForState,
      checkoutFrom,
      checkoutPlanId
    );
  };

  return (
    <button
      onClick={login}
      className={`w-full flex items-center justify-center bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-semibold ${
        size === "lg" ? "gap-3 py-4 px-5 text-lg" : "gap-2 py-3 px-4 text-base"
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
